// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { quantityInputStyle } from "@flip/App/SharedStyle/QuantityInputStyle";
import { constants } from "@flip/XDL/constants";
import { mergeWithCss, xMask, xScreen } from "@flip/XDL/Utilities";
import color from "@flip/XDL/Utilities/color";
import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import { Icon, Typography } from "@insite/mobius/index";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
    // XNG-Changes
    showActions?: boolean;
    showLabel?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;

    // XNG-Changes
    disabledTextField?: TextFieldPresentationProps;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
            css: css`
                ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                    display: none;
                }
            `,
        },
    },
    // XNG-Changes
    disabledTextField: {
        cssOverrides: {
            formField: css`
                max-width: 92px;
            `,
            inputSelect: css`
                width: 100%;
                max-width: 92px;
                height: 26px;
                text-align: center;
                border: 1px solid ${color("common.ui6")};
                background: ${color("common.ui14")};
                &:disabled {
                    color: ${color("text.main")};
                    background: ${color("common.ui4")};
                }
                &:hover {
                    border: 1px solid ${color("common.ui6")};
                }
            `,
        },
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    showActions,
    showLabel,
}) => {
    const localOnQtyOrderedChange = (value: string) => {
        // XNg-Change: apply mask
        const inputQty = Number(xMask.qty(value));
        if (Number.isNaN(inputQty)) {
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );
    // <XNG-Changes>
    const textFieldId = `cartLine-qty-orderd-input-${uniqueId()}`;
    const qtyInputProps = {
        label,
        value,
        type: "qty",
        min: 0,
        max: 999999,
        maxLength: 6,
        "data-test-selector": `${cartLine.id}-cartline_qty`,
        onClick: (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => event.currentTarget.focus(),
        onChange: changeHandler,
        onKeyDown: keyDownHandler,
        onBlur: blurHandler,
        disabled: !cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id],
        id: textFieldId,
    };
    // XNG-Changes: FLIP-1027
    const updateQtyByAction = (type: "increase" | "decrease") => {
        const event = { currentTarget: { value: "" } };
        const multiQty = parseInt(cartLine?.properties.multipleSaleQty as string, 10);
        if (multiQty > 0) {
            event.currentTarget.value = `${Number(value) + (type === "increase" ? multiQty : -multiQty)}`;
            localOnQtyOrderedChange(`${Number(value) + (type === "increase" ? multiQty : -multiQty)}`);
            return;
        }
        event.currentTarget.value = `${Number(value) + (type === "increase" ? 1 : -1)}`;
        localOnQtyOrderedChange(`${Number(value) + (type === "increase" ? 1 : -1)}`);
    };
    // </XNG-Changes>
    const [styles] = React.useState(() => mergeWithCss(cartLineQuantityStyles, extendedStyles));
    // <XNG-Changes>

    return (
        <>
            {showLabel && <Typography {...styles.editableQuantityTextField?.labelProps}>{translate("Qty")}</Typography>}
            <StyledWrapper {...quantityInputStyle?.wrapper}>
                <Clickable
                    {...quantityInputStyle?.actionClickable}
                    disabled={Number(value) <= 1 || !editable || isOutOfStock(cartLine)}
                    onClick={() => updateQtyByAction("decrease")}
                    data-test-selector="product_qtyOrdered_decrease"
                    aria-label="decrease quantity"
                >
                    <Icon src={Minus} size={12} />
                </Clickable>
                <VisuallyHidden as="label" htmlFor={textFieldId}>
                    {translate("Qty")}
                </VisuallyHidden>

                <TextField
                    {...qtyInputProps}
                    {...quantityInputStyle?.quantityOrderedTextField}
                    disabled={!editable || isOutOfStock(cartLine)}
                />
                <Clickable
                    {...quantityInputStyle?.actionClickable}
                    disabled={Number(value) >= 999999 || !editable || isOutOfStock(cartLine)}
                    onClick={() => updateQtyByAction("increase")}
                    data-test-selector="product_qtyOrdered_increase"
                    aria-label="increase quantity"
                >
                    <Icon src={Plus} size={12} />
                </Clickable>
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
